import { useCallback, useMemo, useState } from 'react';

import {
  ErrorCodes,
  getIntegrationNameFromServiceId,
  getIsHrisIntegration,
  getServiceIdFromIntegration,
} from '@aline/common';
import type { ApideckVaultOptions, Connection as VaultConnection } from '@apideck/vault-js';
import { useVault } from '@apideck/vault-react';
import type { Reference } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import retry from 'async-retry';
import { isAfter } from 'date-fns';
import { uniqBy } from 'lodash';
import { keys } from 'ramda';
import { isProd } from 'src/config';
import { useIsOrgAdmin } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { DSConsentType, getDocuSignConsentURI, useLogAndDispatchErrorSnack } from 'src/helpers';

import { createFolderSyncJobsForConnectionKey } from './folder-sync';
import { useRefetchInbox } from './inbox-notifications';

export function useIntegrateDocuSign(): {
  integrateDocuSign: (docuSignApiId: UUID, consentType: DSConsentType) => Promise<void>;
} {
  const [integrateOrg] = Apollo.useIntegrateDocuSignForOrgMutation();
  const [integrateUser] = Apollo.useIntegrateDocuSignForUserMutation();

  return {
    integrateDocuSign: async (docuSignApiId, consentType) => {
      switch (consentType) {
        case DSConsentType.INDIVIDUAL: {
          const { data } = await integrateUser({ variables: { docuSignApiId } });
          if (!data) return;

          const docuSignAuthUri = getDocuSignConsentURI({
            individualConsent: true,
            randomState: data.integrateDocuSignForUser.token,
          });

          return window.location.assign(docuSignAuthUri);
        }
        case DSConsentType.ADMIN: {
          const { data } = await integrateOrg({ variables: { docuSignApiId } });
          if (!data) return;

          const docuSignAuthUri = getDocuSignConsentURI({
            individualConsent: false,
            randomState: data.integrateDocuSignForOrg.token,
          });

          return window.location.assign(docuSignAuthUri);
        }
      }
    },
  };
}

export enum IntegrationState {
  ERROR = 'Integration errored out',
  INTEGRATED = 'Integration integrated',
  SUCCESS = 'Integration initialized successfully',
  UNINTEGRATED = 'Integration uninitialized',
}

const CONNECTION_STATE_SETTLE_RETRY = {
  factor: 1.6,
  maxTimeout: 1_500,
  minTimeout: 400,
  randomize: true,
  retries: 4,
};

const PAIRED_CRM_INTEGRATIONS = new Set<Apollo.Integration>([
  Apollo.Integration.HubSpot,
  Apollo.Integration.SalesForce,
]);

type ExpectedConnectionState = 'callable' | 'gone';

/**
 * CRMs that need setup on their own side before Aline can connect (Salesforce: the Aline package
 * and its permission set), so their first connection pauses on the requirements dialog.
 */
export const CRMS_WITH_CONNECT_REQUIREMENTS = [Apollo.Integration.SalesForce] as const;

export type CrmWithConnectRequirements = (typeof CRMS_WITH_CONNECT_REQUIREMENTS)[number];

export function getHasCrmConnectRequirements(
  integration: Apollo.Integration
): integration is CrmWithConnectRequirements {
  return CRMS_WITH_CONNECT_REQUIREMENTS.some(crm => crm === integration);
}

export interface CrmConnectRequest {
  readonly integration: Apollo.Integration;
  readonly onCancel: () => void;
  readonly onConfirm: () => Promise<void>;
}

export const CRM_CONNECT_ADMIN_ONLY_MESSAGE = 'You must be an Aline admin to connect a CRM.';

export function useAlineCrmSession() {
  /**
   * Every path a Vault session can end on re-reads the connection list: the sync below through
   * `refetchQueries`, and the others explicitly. The server settles the bell's CRM notifications
   * from that same read, so the bell updates through its subscription.
   */
  const [getCrmConnections] = Apollo.useGetCrmConnectionsLazyQuery({ fetchPolicy: 'network-only' });
  const refetchInbox = useRefetchInbox();
  // The server settles the CRM notifications while answering the list, so the inbox reads after it.
  const refetchCrmState = useCallback(async () => {
    await getCrmConnections();
    await refetchInbox();
  }, [getCrmConnections, refetchInbox]);
  // The sync re-probes the CRM's grants, so a problem it finds is in the inbox right after.
  const [syncCrmPairing] = Apollo.useSyncCrmPairingMutation({
    awaitRefetchQueries: true,
    refetchQueries: [Apollo.GetCrmConnectionsDocument, Apollo.GetInboxNotificationsDocument],
  });
  // CRM Vault sessions are admin-only on the server, so members never get the Connect or Manage path.
  const { isOrgAdmin } = useIsOrgAdmin();
  const { allConnections } = useUserIntegrations();
  const [pendingIntegration, setPendingIntegration] = useState<Apollo.Integration | null>(null);
  const onError = useLogAndDispatchErrorSnack({
    message: 'Could not finish updating this CRM connection.',
  });
  const settleConnectionState = useCallback(
    async ({
      expectedState,
      integration,
    }: {
      expectedState: ExpectedConnectionState;
      integration: Apollo.Integration;
    }) => {
      try {
        await retry(async () => {
          const { data } = await getCrmConnections();
          const listed = data?.integrationConnections.crm.find(
            connection => connection.integration === integration
          );
          // Vault "Disconnect" leaves the connection listed as `added`; disable and delete also count.
          const isSettled =
            expectedState === 'callable'
              ? listed?.state === Apollo.ApiDeckConnectionState.Callable
              : !listed ||
                listed.enabled === false ||
                listed.state === Apollo.ApiDeckConnectionState.Available ||
                listed.state === Apollo.ApiDeckConnectionState.Added;
          if (!isSettled)
            throw new Error(`${integration} still reads ${listed?.state ?? 'unlisted'}.`);
        }, CONNECTION_STATE_SETTLE_RETRY);
        return true;
      } catch {
        return false;
      }
    },
    [getCrmConnections]
  );
  const onVaultConnection = useCallback(
    async ({
      connection,
      expectedState,
    }: {
      connection: VaultConnection;
      expectedState: ExpectedConnectionState;
    }) => {
      try {
        const integration = getIntegrationNameFromServiceId(connection.service_id);
        if (!integration || !PAIRED_CRM_INTEGRATIONS.has(integration) || !isOrgAdmin) {
          await refetchCrmState();
          return;
        }

        const settled = await settleConnectionState({ expectedState, integration });
        /**
         * A list that never caught up cannot be synced against (a stale `callable` after a
         * disconnect would re-pair the org), but what it does say is still shown right away.
         */
        if (settled) await syncCrmPairing({ variables: { integration } });
        else await refetchCrmState();
      } catch (error) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    },
    [isOrgAdmin, onError, refetchCrmState, settleConnectionState, syncCrmPairing]
  );

  const vaultCallbacks = useMemo<Omit<AlineApiDeckSessionOptions, 'unifiedApi'>>(
    () => ({
      onClose: refetchCrmState,
      onConnectionChange: connection => {
        if (connection.state === 'callable') {
          void onVaultConnection({ connection, expectedState: 'callable' });
          return;
        }

        const isGoneState =
          connection.enabled === false ||
          connection.state === 'added' ||
          connection.state === 'available';
        if (isGoneState) {
          void onVaultConnection({ connection, expectedState: 'gone' });
          return;
        }

        void refetchCrmState();
      },
      onConnectionDelete: connection =>
        void onVaultConnection({ connection, expectedState: 'gone' }),
    }),
    [onVaultConnection, refetchCrmState]
  );
  const { openVault: openCrmVault } = useAlineApiDeckSession({
    ...vaultCallbacks,
    unifiedApi: 'crm',
  });
  const { openVault: openHrisVault } = useAlineApiDeckSession({
    ...vaultCallbacks,
    unifiedApi: 'hris',
  });
  const openVault = useCallback(
    async (serviceId: string) => {
      const integration = getIntegrationNameFromServiceId(serviceId);
      if (getIsHrisIntegration(integration)) return openHrisVault(serviceId);
      if (!integration || getIsIntegrationCallable(allConnections, integration))
        return openCrmVault(serviceId);

      // The Aline-admin gate is enforced on the control, not read in the dialog.
      if (!getHasCrmConnectRequirements(integration)) return openCrmVault(serviceId);
      setPendingIntegration(integration);
    },
    [allConnections, openCrmVault, openHrisVault]
  );
  const crmConnectRequest = useMemo<CrmConnectRequest | null>(() => {
    if (!pendingIntegration) return null;
    return {
      integration: pendingIntegration,
      onCancel: () => setPendingIntegration(null),
      onConfirm: async () => {
        setPendingIntegration(null);
        await openCrmVault(getServiceIdFromIntegration(pendingIntegration));
      },
    };
  }, [openCrmVault, pendingIntegration]);

  return { canConnectCrm: isOrgAdmin, crmConnectRequest, openVault };
}

const API_DECK_UNIFIED_API_BY_VAULT_API = {
  crm: Apollo.ApiDeckUnifiedApi.Crm,
  'file-storage': Apollo.ApiDeckUnifiedApi.FileStorage,
  hris: Apollo.ApiDeckUnifiedApi.Hris,
} as const;

interface AlineApiDeckSessionOptions extends Pick<
  ApideckVaultOptions,
  'onClose' | 'onConnectionChange' | 'onConnectionDelete'
> {
  unifiedApi: keyof typeof API_DECK_UNIFIED_API_BY_VAULT_API;
}

export function useAlineApiDeckSession(options: AlineApiDeckSessionOptions) {
  const { open } = useVault();
  const [createApiDeckSession] = Apollo.useCreateApideckSessionMutation();
  const [session, setSession] = useState<Apollo.ApiDeckSession | null>(null);
  const unifiedApi = API_DECK_UNIFIED_API_BY_VAULT_API[options.unifiedApi];
  const createSession = useCallback(
    async (serviceId: string) => {
      const { data } = await createApiDeckSession({ variables: { unifiedApi } });
      if (!data) return;

      open({
        serviceId,
        token: data.createApiDeckSession.sessionToken,
        ...options,
      });
      setSession(data.createApiDeckSession);
    },
    [createApiDeckSession, open, options, unifiedApi]
  );
  const openVault = useCallback(
    async (serviceId: string) => {
      // Reuse the cached session while it is still valid; a spent one is replaced.
      if (!session || !isAfter(new Date(session.expiresAt), new Date()))
        return createSession(serviceId);

      open({
        serviceId,
        token: session.sessionToken,
        ...options,
      });
    },
    [createSession, open, options, session]
  );

  return { openVault };
}

export function useUserIntegrations(
  options?: Parameters<typeof Apollo.useGetCrmConnectionsQuery>[0]
) {
  const { data, loading } = Apollo.useGetCrmConnectionsQuery(options);

  return useMemo(() => {
    const crm = data?.integrationConnections.crm ?? [];
    const hrisConnections = data?.integrationConnections.hris ?? null;
    const allConnections = [...crm, ...(hrisConnections ?? [])];
    return {
      allConnections,
      callableConnections: allConnections.filter(
        connection => connection.state === Apollo.ApiDeckConnectionState.Callable
      ),
      hrisConnections,
      initialLoading: loading && !data,
      loading,
    };
  }, [data, loading]);
}

/** Whether Aline's Apideck app has an integration ready; see `getIsIntegrationReady`. */
export function useIntegrationReadiness(integration: Apollo.Integration): {
  readonly ready: boolean;
} {
  const isHris = getIsHrisIntegration(integration);
  const { hrisConnections, initialLoading } = useUserIntegrations({ skip: !isHris });

  return useMemo(
    () => ({ ready: getIsIntegrationReady({ hrisConnections, initialLoading, integration }) }),
    [hrisConnections, initialLoading, integration]
  );
}

/**
 * A CRM is always ready. An HRIS is once Aline's Apideck app has it configured with the
 * provider's production OAuth app, not the development one (HiBob's Credentials Type, Gusto's demo
 * server): switching it in the Apideck dashboard is what makes it ready, with no deploy. One
 * already connected stays ready. A `null` list means Vault could not list the HRIS connectors, so
 * the HRIS is not shown as coming soon when it may well be connected; a list without the HRIS
 * reads as not yet. Likewise an unknown credentials mode (the authorize probe failed) reads as
 * ready, so a probe blip never disables Connect.
 */
export function getIsIntegrationReady({
  hrisConnections,
  initialLoading,
  integration,
}: {
  readonly hrisConnections: readonly Apollo.ApiDeckConnectionSimpleFragment[] | null;
  readonly initialLoading: boolean;
  readonly integration: Apollo.Integration;
}): boolean {
  if (!getIsHrisIntegration(integration)) return true;
  if (initialLoading) return false;
  if (!hrisConnections) return true;
  const connection = hrisConnections.find(row => row.integration === integration);
  if (connection?.state === Apollo.ApiDeckConnectionState.Callable) return true;
  if (connection?.integrationState !== Apollo.ApiDeckIntegrationState.Configured) return false;
  return connection.credentialsMode !== Apollo.ApiDeckCredentialsMode.Development;
}

export interface IntegrationVaultGate {
  readonly blocked: boolean;
  readonly muted: boolean;
  readonly reason: string | null;
}

/**
 * Only production refuses a coming-soon integration and draws it muted: staging and local still
 * connect it with development credentials. Only admins connect.
 */
export function getIntegrationVaultGate({
  canConnectCrm,
  production = isProd,
  ready,
}: {
  readonly canConnectCrm: boolean;
  readonly ready: boolean;
  readonly production?: boolean;
}): IntegrationVaultGate {
  if (!canConnectCrm)
    return { blocked: true, muted: !ready && production, reason: CRM_CONNECT_ADMIN_ONLY_MESSAGE };
  if (!ready) return { blocked: production, muted: production, reason: null };
  return { blocked: false, muted: false, reason: null };
}

const TEST_ACCOUNT_NOTE = 'Development app. Only connect a test account.';

const DEVELOPMENT_HELP_NOTES: Partial<Record<Apollo.Integration, string>> = {
  [Apollo.Integration.Gusto]: TEST_ACCOUNT_NOTE,
  [Apollo.Integration.HiBob]: TEST_ACCOUNT_NOTE,
  [Apollo.Integration.Rippling]: TEST_ACCOUNT_NOTE,
};

const CONNECTION_NOTES: Partial<
  Record<Apollo.Integration, Record<'development' | 'production', string>>
> = {
  [Apollo.Integration.HiBob]: {
    development:
      'Disconnecting does not uninstall it: remove it in HiBob under Marketplace › In development.',
    production: 'Disconnecting does not uninstall it: remove it in HiBob too.',
  },
};

/**
 * The hover note on an integration's Connect control, outside production only: Gusto, HiBob and
 * Rippling connect with the provider's development app.
 */
export function getIntegrationHelpNote({
  integration,
  production = isProd,
}: {
  readonly integration: Apollo.Integration;
  readonly production?: boolean;
}): string | null {
  if (production) return null;
  return DEVELOPMENT_HELP_NOTES[integration] ?? null;
}

/** The note in an integration's connection details, such as where to uninstall HiBob. */
export function getIntegrationConnectionNote({
  integration,
  production = isProd,
}: {
  readonly integration: Apollo.Integration;
  readonly production?: boolean;
}): string | null {
  return CONNECTION_NOTES[integration]?.[production ? 'production' : 'development'] ?? null;
}

export function useFileStorageConnections(
  options?: Parameters<typeof Apollo.useGetFileStorageConnectionsQuery>[0]
) {
  const { data, error, loading, previousData, refetch } =
    Apollo.useGetFileStorageConnectionsQuery(options);
  const connectionsData = data ?? previousData;
  return {
    error,
    fileStorageConnections: connectionsData?.fileStorageConnections ?? [],
    initialLoading: loading && !connectionsData,
    loading,
    refetch,
  };
}

export function useWorkflowMappedCrmIntegrations(workflowId: UUID | null) {
  const { data, loading } = Apollo.useGetWorkflowMappedCrmIntegrationsQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });

  return {
    loading,
    mappedCrmIntegrations: new Set<Apollo.Integration>(data?.workflow?.mappedCrmIntegrations ?? []),
  };
}

export function getIsApiDeckConnectionCallable(
  connection: Pick<Apollo.ApiDeckConnection, 'state' | '__typename'>
): boolean {
  return connection.state === Apollo.ApiDeckConnectionState.Callable;
}

/** Whether the listed connections hold a callable one for this integration. */
export function getIsIntegrationCallable(
  connections: readonly Pick<Apollo.ApiDeckConnection, 'integration' | 'state' | '__typename'>[],
  integration: Apollo.Integration | null
): boolean {
  const connection = connections.find(candidate => candidate.integration === integration);
  return connection ? getIsApiDeckConnectionCallable(connection) : false;
}

export function useGetIntegrationOpportunities(
  options: Parameters<typeof Apollo.useGetIntegrationOpportunitiesQuery>[0]
) {
  const { data, ...rest } = Apollo.useGetIntegrationOpportunitiesQuery(options);
  return {
    ...rest,
    nextCursor: data?.integrationOpportunities.nextCursor ?? null,
    opportunities: data?.integrationOpportunities.opportunities ?? [],
  };
}

export function useWorkflowLinkedOpportunity({
  integration,
  workflowId,
}: {
  integration: Apollo.Integration | null;
  workflowId: UUID | null;
}) {
  const { data, loading } = Apollo.useGetWorkflowLinkedOpportunitiesQuery({
    skip: !workflowId,
    variables: {
      workflowId: workflowId ?? '',
    },
  });

  return {
    linkedOpportunity:
      data?.workflow?.linkedOpportunities.find(opp => opp.integration === integration) ?? null,
    loading,
  };
}

export function useIntegrationField({
  integration,
  opportunityId,
  path,
}: {
  integration: Apollo.Integration;
  opportunityId: UUID | null;
  path: string;
}) {
  const { data, loading } = Apollo.useGetIntegrationFieldQuery({
    skip: !opportunityId,
    variables: {
      input: {
        opportunityId: opportunityId ?? '',
        path,
        serviceId: getServiceIdFromIntegration(integration),
      },
    },
  });

  return {
    integrationField: data?.integrationField ?? null,
    loading,
  };
}

export type LinkedOpportunitySubset = Pick<
  Apollo.LinkedOpportunity,
  'integration' | 'opportunityId' | '__typename'
>;

/**
 * `workflowId` names the workflow the record is linked to, so the server can notify the org when
 * the record turns out to be deleted in the CRM or HRIS (a deal, an opportunity, an employee).
 */
export function useGetApideckOpportunity(
  linkedOpportunity: LinkedOpportunitySubset | null,
  options?: Omit<NonNullable<Parameters<typeof Apollo.useGetOpportunityQuery>[0]>, 'variables'> & {
    workflowId?: UUID;
  }
) {
  const { workflowId, ...queryOptions } = options ?? {};
  const { data, error, loading } = Apollo.useGetOpportunityQuery({
    ...queryOptions,
    skip: !linkedOpportunity,
    variables: {
      opportunityId: linkedOpportunity?.opportunityId ?? '',
      serviceId: linkedOpportunity
        ? getServiceIdFromIntegration(linkedOpportunity.integration)
        : '',
      workflowId,
    },
  });
  const recordDeleted =
    CombinedGraphQLErrors.is(error) &&
    error.errors.some(
      graphQLError => graphQLError.extensions?.code === ErrorCodes.CRM_RECORD_NOT_FOUND
    );
  return { apiDeckOpportunity: data?.apiDeckOpportunity ?? null, loading, recordDeleted };
}

export function useGetApideckOpportunitySimple(
  options: Parameters<typeof Apollo.useGetOpportunitySimpleQuery>[0]
) {
  const { data, loading } = Apollo.useGetOpportunitySimpleQuery(options);
  return { apiDeckOpportunity: data?.apiDeckOpportunity ?? null, loading };
}

export function useOnFolderSyncJobUpdateSubscription() {
  const { data, loading } = Apollo.useOnFolderSyncJobUpdateSubscription({
    onData: ({ client, data }) => {
      const folderSyncJobsKey: keyof Apollo.Query = 'folderSyncJobs';
      const deletedIds = new Set(data.data?.folderSyncJobUpdate.deletedFolderSyncJobIds ?? []);
      if (deletedIds.size > 0) {
        client.cache.modify({
          fields: {
            [folderSyncJobsKey]: (existingJobs: readonly Reference[] = [], { readField }) => {
              return existingJobs.filter(job => !deletedIds.has(readField('id', job) ?? ''));
            },
          },
        });
        Object.values(Apollo.FileStorageIntegration).forEach(integration => {
          client.cache.modify({
            fields: {
              [createFolderSyncJobsForConnectionKey(integration)]: (
                existingJobs: readonly Reference[] = [],
                { readField }
              ) => {
                return existingJobs.filter(job => !deletedIds.has(readField('id', job) ?? ''));
              },
            },
          });
        });

        // Evict the deleted jobs from the cache entirely.
        deletedIds.forEach(id => {
          client.cache.evict({ id: client.cache.identify({ __typename: 'FolderSyncJob', id }) });
        });
        client.cache.gc();
      }

      // Handle updates/additions.
      const jobsByIntegration =
        data.data?.folderSyncJobUpdate.folderSyncJobs.reduce<
          Partial<
            Record<
              Apollo.FileStorageIntegration,
              Apollo.OnFolderSyncJobUpdateSubscription['folderSyncJobUpdate']['folderSyncJobs']
            >
          >
        >((acc, job) => {
          if (!job.integration) return acc;

          const jobs = acc[job.integration] ?? [];
          acc[job.integration] = [...jobs, job];

          return acc;
        }, {}) ?? {};
      keys(jobsByIntegration).forEach(integration => {
        const jobs = jobsByIntegration[integration] ?? [];
        const newJobs =
          jobs.flatMap(job => {
            const ref = client.cache.writeFragment({
              data: job,
              fragment: Apollo.FolderSyncJobSimpleFragmentDoc,
            });
            return ref ? [ref] : [];
          }) ?? [];

        client.cache.modify({
          fields: {
            [folderSyncJobsKey]: (existingJobs: readonly Reference[] = [], { readField }) => {
              return uniqBy([...existingJobs, ...newJobs], job => readField('id', job));
            },
            [createFolderSyncJobsForConnectionKey(integration)]: (
              existingJobs: readonly Reference[] = [],
              { readField }
            ) => {
              return uniqBy([...existingJobs, ...newJobs], job => readField('id', job));
            },
          },
        });
      });
    },
  });

  return { data, loading };
}
